import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Check, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../../stores/authStore";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { ErrorAlert } from "../../../components/ui/ErrorAlert";
import { useValidatedForm } from "../../../hooks/useValidatedForm";
import { loginSchema, type LoginFormData } from "../../../schemas/validation";
import { MolduraDeAcesso } from "./MolduraDeAcesso";
import { entradaEquipe } from "../../equipe/config";

/** O que a recuperação de senha deixa para o login: quem acabou de trocar. */
interface VindoDaRecuperacao {
  login?: string;
  senhaRedefinida?: boolean;
}

const Login = () => {
  const navigate = useNavigate();
  const recuperacao = (useLocation().state ?? {}) as VindoDaRecuperacao;
  const { login: authLogin, isLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const form = useValidatedForm<LoginFormData>({
    schema: loginSchema,
    defaultValues: {
      login: recuperacao.login ?? "",
      senha: "",
    },
    onSubmit: async (data) => {
      await authLogin(data);
      navigate("/");
    },
    onError: (error) => {
      console.error("Login error:", error);
    },
  });

  return (
    <MolduraDeAcesso
      titulo="Portal de Chamados"
      subtitulo="Acesse sua conta para continuar"
      rodape={
        // Discreto de propósito: quem usa este login é o cliente. A equipe
        // entra pela própria página, com a conta Microsoft.
        entradaEquipe.configurada && (
          <Link to="/equipe" className="underline-offset-2 hover:text-gray-700 hover:underline">
            Acesso da equipe T4i
          </Link>
        )
      }
    >
      {recuperacao.senhaRedefinida && (
        <p className="mb-6 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/70 p-3 text-sm text-emerald-800">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          Senha alterada. Entre com a senha nova.
        </p>
      )}

      <form onSubmit={form.handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label
              htmlFor="login"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Usuário
            </label>
            <Input
              {...form.register("login")}
              id="login"
              type="text"
              placeholder="Digite seu login"
              className={`w-full ${
                form.getFieldState("login").isInvalid
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : ""
              }`}
            />
            {form.getFieldState("login").showError && (
              <p className="text-red-600 text-sm mt-1" id="login-error">
                {form.getFieldError("login")}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="senha"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Senha
            </label>
            <div className="relative">
              <Input
                {...form.register("senha")}
                id="senha"
                autoFocus={Boolean(recuperacao.login)}
                type={showPassword ? "text" : "password"}
                placeholder="Digite sua senha"
                className={`w-full pr-12 ${
                  form.getFieldState("senha").isInvalid
                    ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                    : ""
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {form.getFieldState("senha").showError && (
              <p className="text-red-600 text-sm mt-1" id="senha-error">
                {form.getFieldError("senha")}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-blue-800 to-blue-600 text-white font-medium py-3 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
          loading={isLoading || form.isSubmitting}
          disabled={isLoading || form.isSubmitting}
        >
          {isLoading || form.isSubmitting ? "Entrando..." : "Entrar"}
        </Button>

        {form.submitError && (
          <ErrorAlert
            title="Erro no Login"
            message={form.submitError}
            onClose={form.clearSubmitError}
            variant="error"
          />
        )}

        <div className="text-center pt-4">
          <Link
            to="/reset-password"
            className="text-sm text-blue-600 hover:text-blue-500 font-medium transition-colors"
          >
            Esqueci minha senha
          </Link>
        </div>
      </form>
    </MolduraDeAcesso>
  );
};

export default Login;
